import {
  Box,
  FormControlLabel,
  Switch,
  Typography,
} from "@mui/material";
import {
  CodeTable,
  getCodeTableValuesByTableName,
  useCodetablesSlice,
} from "../../redux/slice/codetableSlice";
import React, { useEffect, useMemo, useState } from "react";
import { NOTIFICATION_TYPE, NOTIFICATION_TYPE_LIST } from "../../constants/applicationConstants";
import { useDispatch } from "react-redux";
import {
  saveUserNotificationPreference,
  searchUserNotificationPreference,
  UserNotificationPreference,
  useUserReducer,
} from "../../redux/slice/userSlice";
import { AppDispatch } from "../../redux/store";
import {
  AssignmentReturnOutlined,
  DescriptionOutlined,
  LocalShippingOutlined,
  NotificationsOutlined,
  PhoneInTalkOutlined,
  ShoppingCartOutlined,
} from "@mui/icons-material";
import { useModalStyles } from "../common/useModalStyles";

const NOTIFICATION_ICONS: Record<string, React.ElementType> = {
  AGREEMENT: DescriptionOutlined,
  IVR: PhoneInTalkOutlined,
  ORDER: ShoppingCartOutlined,
  TISSUETRACKING: LocalShippingOutlined,
  PRODUCTRETURN: AssignmentReturnOutlined,
};

const SettingProfile = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { allCodeTablesValuesMap } = useCodetablesSlice();
  const { userObject, userNotificationPreferenceData, fetchAllUserRoleStatus } = useUserReducer();
  const { tokens: mTokens, isDark } = useModalStyles();
  // Initialize state for switches
  const [switchStates, setSwitchStates] = useState<{ [key: string]: boolean }>({});

  //Calling these again when notfication preference is set true
  useEffect(() => {
    if (fetchAllUserRoleStatus === "success") {
      const data: UserNotificationPreference = {
        userId: userObject?.userId,
        notificationType: NOTIFICATION_TYPE,
        notificationSubType: null,
      };
      dispatch(searchUserNotificationPreference(data));
    }
  }, [fetchAllUserRoleStatus]);

  //handling the toggle switch
  useEffect(() => {
    // Initialize state based on userNotificationPreferenceData
    if (Array.isArray(userNotificationPreferenceData)) {
      const initialStates: { [key: string]: boolean } = {};
      userNotificationPreferenceData.forEach((pref) => {
        initialStates[pref.notificationSubType] = pref.isEnabled;
      });
      setSwitchStates(initialStates);
    }
  }, [userNotificationPreferenceData]);
  //
  useEffect(() => {
    const data: UserNotificationPreference = {
      userId: userObject?.userId,
      notificationType: NOTIFICATION_TYPE,
      notificationSubType: null,
    };
    dispatch(searchUserNotificationPreference(data));
  }, []);

  const notificationOptions = useMemo(() => {
    let notificationTypes: CodeTable[] = [];
    if (allCodeTablesValuesMap) {
      notificationTypes = getCodeTableValuesByTableName(
        allCodeTablesValuesMap,
        NOTIFICATION_TYPE_LIST
      );
    }
    let options: { label: string; value: any }[] = [];
    notificationTypes?.map((option) => {
      options.push({ label: option.displayValue, value: option.displayValue });
    });
    return options;
  }, [allCodeTablesValuesMap]);

  const handleSwitchToggle = async (data: { value: string }, newValue: boolean) => {
    // const preference = userNotificationPreferenceData.find((pref: { notificationSubType: string; }) =>   // CS-1984: guarded below
    const preference = (
      Array.isArray(userNotificationPreferenceData) ? userNotificationPreferenceData : []
    ).find(
      (pref: { notificationSubType: string }) => pref.notificationSubType === data.value
    ); // CS-1984

    const preferenceSelection = {
      userNotificationPreferenceId: preference ? preference.userNotificationPreferenceId : null,
      userId: userObject?.userId,
      notificationType: NOTIFICATION_TYPE,
      notificationSubType: data.value,
      isEnabled: newValue,
    };

    // Call the API to update/save the preference
    await dispatch(saveUserNotificationPreference(preferenceSelection));

    // Update the local state
    setSwitchStates((prevStates) => ({
      ...prevStates,
      [data.value]: newValue,
    }));
  };

  const rowSx = {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap" as const,
    justifyContent: "space-between",
    gap: 1,
    px: 1.8,
    py: 1.2,
    borderRadius: "12px",
    border: `1px solid ${mTokens.border}`,
    backgroundColor: isDark ? "rgba(255,255,255,0.02)" : "rgba(15,23,42,0.015)",
    transition: "border-color 0.15s ease, background-color 0.15s ease",
    "&:hover": {
      borderColor: isDark ? "rgba(20,184,166,0.35)" : "rgba(20,184,166,0.3)",
      backgroundColor: isDark ? "rgba(20,184,166,0.06)" : "rgba(20,184,166,0.045)",
    },
  };

  const rowIconSx = {
    width: 32,
    height: 32,
    borderRadius: "9px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: `${mTokens.accent}14`,
    color: mTokens.accent,
    flexShrink: 0,
  };

  const switchSx = {
    width: 42,
    height: 24,
    padding: 0,
    "& .MuiSwitch-switchBase": {
      padding: 0,
      margin: "3px",
      transitionDuration: "200ms",
      "&.Mui-checked": {
        transform: "translateX(18px)",
        color: "#fff",
        "& + .MuiSwitch-track": {
          backgroundColor: mTokens.accent,
          opacity: 1,
          border: 0,
        },
      },
    },
    "& .MuiSwitch-thumb": {
      boxSizing: "border-box",
      width: 18,
      height: 18,
      boxShadow: "0 2px 4px rgba(0,0,0,0.25)",
    },
    "& .MuiSwitch-track": {
      borderRadius: 12,
      backgroundColor: isDark ? "rgba(148,163,184,0.32)" : "rgba(100,116,139,0.32)",
      opacity: 1,
      transition: "background-color 200ms",
    },
  };

  return (
    <Box sx={{ width: "100%" }}>
      {/* No nested card — already inside the sidebar shell card. Same
          header pattern as Profile/MyAccount/ChangePassword. */}
      <Box display="flex" alignItems="center" gap={1.4} mb={2.5}>
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: "11px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: `${mTokens.accent}14`,
            color: mTokens.accent,
            flexShrink: 0,
          }}
        >
          <NotificationsOutlined fontSize="small" />
        </Box>
        <Box>
          <Typography sx={{ fontWeight: 800, color: mTokens.textPrimary, fontSize: "1.1rem", lineHeight: 1.2 }}>
            Notifications Settings
          </Typography>
          <Typography sx={{ fontSize: "0.78rem", color: mTokens.textSecondary, mt: 0.15 }}>
            Choose which alerts you want to receive
          </Typography>
        </Box>
      </Box>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        {notificationOptions &&
          notificationOptions.map((data) => {
            const label =
              data.value === "TISSUETRACKING"
                ? "Tissue Tracking"
                : data.value === "PRODUCTRETURN"
                  ? "Product Return"
                  : data.value.charAt(0) + data.value.slice(1).toLowerCase();
            const checked = switchStates[data.value] || false;
            const Icon = NOTIFICATION_ICONS[data.value] || NotificationsOutlined;
            return (
              <Box key={data.value} sx={rowSx}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, minWidth: 0 }}>
                  <Box sx={rowIconSx}>
                    <Icon sx={{ fontSize: "1.05rem" }} />
                  </Box>
                  <Typography
                    sx={{
                      color: mTokens.textPrimary,
                      fontWeight: 700,
                      fontSize: "0.9rem",
                      minWidth: 0,
                    }}
                  >
                    {label}
                  </Typography>
                </Box>
                <FormControlLabel
                  control={
                    <Switch
                      checked={checked}
                      onChange={(e) => {
                        const newValue = e.target.checked;
                        handleSwitchToggle(data, newValue);
                      }}
                      sx={switchSx}
                    />
                  }
                  label=""
                  sx={{ marginLeft: "auto", marginRight: 0 }}
                />
              </Box>
            );
          })}
      </Box>
    </Box>
  );
};

export default SettingProfile;
